import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import isEqual from 'lodash/isEqual';

import { OrderService } from '../../../services/order.service';
import { Order } from '../../../models/order.model';
import { AuthService } from '../../../services/auth.service';
import { OrderTypes } from '../../../enums/order-types';
import { ModalService } from '../../../services/modal.service';
import { isPaymentMethodDefined } from '../../../shared/helpers';

@UntilDestroy()
@Component({
  selector: 'app-cart-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CartSelectComponent implements OnInit {
  @Input() mobile = false;
  @Input() allowAdd: boolean;

  @Input() withOrderWindowTracking = false;

  @Output() selectedOrder = new EventEmitter<Order>();

  // private isNewOrder = false;
  private initialized = false;
  private creatingOrder = false;

  // private orders$: Observable<Order[]>;
  orders: Order[] = [];

  constructor(
    protected orderService: OrderService,
    protected toastr: ToastrService,
    private authService: AuthService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.init();
  }

  onSelectOrder(order: Order): void {
    this.selectOrder(order);
  }

  onAddOrder(): void {
    this.addOrder();
  }

  private init(): void {
    this.orderService.initialized$
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.initialized = result;
        if (this.initialized && !this.withOrderWindowTracking) {
          this.selectOrder();
        }
      });

    if (this.withOrderWindowTracking) {
      this.orderService
        .buyingWindowChanged()
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          if (this.orderService.currentOrderType === OrderTypes.PREORDER_TYPE) {
            this.selectOrder();
          }
        });

      this.orderService.currentOrderType$
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.selectOrder();
        });
    }
  }

  private selectOrder(order?: Order): void {
    if (!this.initialized) {
      return;
    }

    // user selected order
    if (order) {
      this.orderService
        .switchActiveOrder(order)
        .pipe(untilDestroyed(this))
        .subscribe((retOrder) => {
          this.selectedOrder.emit(retOrder);
        });
    } else if (!this.orderService.activeOrder) {
      if (this.orderService.currentOrders.length === 0) {
        this.addOrder();
      } else {
        this.orderService
          .switchActiveOrder(this.orderService.currentOrders[0])
          .pipe(untilDestroyed(this))
          .subscribe((retOrder) => {
            this.selectedOrder.emit(retOrder);
          });
      }
    }
  }

  private addOrder(): void {
    if (!this.allowAdd || this.creatingOrder) {
      return;
    }

    this.creatingOrder = true;
    this.orderService
      .createOrder(this.orderType)
      .pipe(untilDestroyed(this))
      .subscribe((order) => {
        if (
          this.canChangeOrderSettings &&
          order.id > 0 &&
          !order.isWSLR &&
          this.authService.canEditSAP
        ) {
          this.modalService.editOrderSettings(order).then((retOrder) => {
            this.selectedOrder.emit(retOrder || order);
            this.creatingOrder = false;
          });
        } else {
          this.selectedOrder.emit(order);
          this.creatingOrder = false;
        }
      });
  }

  get label(): string {
    return this.orderType === OrderTypes.ECOMMERCE_TYPE
      ? 'CURRENT CART'
      : 'CURRENT ORDER';
  }

  get buttonLabel(): string {
    return this.orderType === OrderTypes.ECOMMERCE_TYPE
      ? 'CREATE NEW CART'
      : 'CREATE NEW ORDER';
  }

  get getterOrders(): Order[] {
    // this is workaround after mobix to rxjs
    if (!isEqual(this.orders, this.orderService.currentOrders)) {
      this.orders = this.orderService.currentOrders;
    }

    return this.orderService.currentOrders;
  }

  get orderType(): string {
    return this.orderService.currentOrderType;
  }

  getOrderLabel(item: Order): string {
    return item.selectLabel;
  }

  get visible(): boolean {
    return (
      this.orderType &&
      this.canOrder &&
      !(
        this.orderType === OrderTypes.PREORDER_TYPE &&
        this.orderService.state.activeOrderWindow?.active !== true
      )
    );
  }

  get activeOrder(): Order {
    return this.orderService.activeOrder;
  }

  get selectedID(): number {
    return this.activeOrder ? this.activeOrder.id : 0;
  }

  // should be present for ng-model binding
  set selectedID(val) {}

  isPaymentDefined(order: Order) {
    return (
      isPaymentMethodDefined(
        order,
        this.authService.user,
        this.authService.canEditSAP
      ) === true
    );
  }

  get canOrder(): boolean {
    return this.authService.canOrder;
  }

  get canChangeOrderSettings(): boolean {
    return this.authService.canEditOrder(this.activeOrder);
  }
}
